// Custom subdomain rules; mirrors NormalizeSubdomain in internal/app/subdomain.go.
// No imports, so it runs under node in the unit tests.

export const MAX_SUBDOMAIN_LENGTH = 40;

const normalize = (raw: string) => raw.trim().toLowerCase();

/** Why a custom subdomain would be refused by the API, or null if it is fine ('' = none). */
export function subdomainError(raw: string): string | null {
  const s = normalize(raw);
  if (s === '') return null;
  if (s.length > MAX_SUBDOMAIN_LENGTH) return `At most ${MAX_SUBDOMAIN_LENGTH} characters.`;
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(s)) return "Use a–z, 0–9 and '-' (not first or last).";
  if (/-[0-9]+$/.test(s)) return 'Must not end in -<number> (reserved for generated names).';
  return null;
}

/** `<subdomain>.<domain>` for a valid subdomain; domain '' = the default domain. Null when there is nothing to show. */
export function customHostLabel(
  subdomain: string,
  domain: string,
  domains: readonly { name: string; is_default: boolean }[],
): string | null {
  const s = normalize(subdomain);
  if (s === '' || subdomainError(s) !== null) return null;
  const name = domain || domains.find((d) => d.is_default)?.name;
  return name ? `${s}.${name}` : null;
}

/** A notice when the deploy did not get the requested subdomain as-is (taken, reserved, or not checkable), or null. */
export function subdomainFallbackNotice(requested: string, fullSubdomain: string | undefined): string | null {
  const s = normalize(requested);
  if (s === '' || fullSubdomain?.startsWith(`${s}.`)) return null;
  // No hostname: the endpoint has no public IP or the DNS record could not be created.
  if (!fullSubdomain) return `Subdomain ${s} was not applied. The deployment log has the reason.`;
  return `${s} was not available, so the app uses ${fullSubdomain}. The deployment log has the reason.`;
}
